// src/components/CitaCalendar.jsx

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../axiosInstance';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';
import {
  Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Box, Typography, Chip, Avatar, IconButton, CircularProgress, Divider, Fade,
  useMediaQuery, Paper, alpha, Snackbar, Alert
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import SubjectIcon from '@mui/icons-material/Subject';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useAuth } from '../context/AuthContext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PhoneIcon from '@mui/icons-material/Phone';
import luxon3Plugin from '@fullcalendar/luxon3';

const CitaCalendar = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const citasSource = Array.isArray(props.citas) ? props.citas : [];
  const {
    citas = [], onListChange, onSendReminder, sendingReminder,
    showReminderButton = false
  } = props;

  const { user } = useAuth();

  const preferTZ = (...cands) => {
    for (const c of cands) {
      const v = (c || '').trim();
      if (!v) continue;
      if (v.toUpperCase() === 'UTC') continue;
      if (v.includes('/')) return v;
      return v;
    }
    return 'America/Tegucigalpa';
  };

  const medicoTzResolved = preferTZ(
    user?.medico?.zona_horaria,
    user?.medico?.timezone,
    citasSource?.[0]?.medico_timezone
  );

  const calendarTz = useMemo(
    () => medicoTzResolved || 'America/Tegucigalpa',
    [medicoTzResolved]
  );

  const showCancelled = props.showCancelled ?? false;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [fadeIn, setFadeIn] = useState(false);
  const [completingAppointment, setCompletingAppointment] = useState(false);
  const [showScrollHelper, setShowScrollHelper] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const calendarRef = useRef(null);
  const [dateRangeText, setDateRangeText] = useState('');
  const [currentView, setCurrentView] = useState(isMobile ? 'timeGridDay' : 'timeGridWeek');

  const showNotification = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const formatHMInTz = (date, tz) =>
    new Intl.DateTimeFormat('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: tz || undefined,
    }).format(date);

  const eventosDelCalendario = useMemo(() => {
    const source = Array.isArray(props.citas) ? props.citas : [];
    return source
      .filter((cita) => showCancelled || cita.estado_cita !== 'Cancelada')
      .map((cita) => {
        const startIso = cita.fecha_hora_cita;
        const startDate = new Date(startIso);
        if (isNaN(startDate.getTime())) return null;

        const endDate = new Date(startDate);
        endDate.setMinutes(startDate.getMinutes() + (cita.duracion_minutos || 30));
        const startIsoUtc = startDate.toISOString();
        const endIsoUtc = endDate.toISOString();

        let backgroundColor, borderColor, textColor, gradientColor;
        switch (cita.estado_cita) {
          case 'Programada':
            backgroundColor = theme.palette.info.main;
            borderColor = theme.palette.info.dark;
            textColor = '#ffffff';
            gradientColor = alpha(theme.palette.info.light, 0.7);
            break;
          case 'Confirmada':
            backgroundColor = '#9b59b6';
            borderColor = '#8e44ad';
            textColor = '#ffffff';
            gradientColor = alpha('#c39bd3', 0.7);
            break;
          case 'Completada':
            backgroundColor = theme.palette.success.main;
            borderColor = theme.palette.success.dark;
            textColor = '#ffffff';
            gradientColor = alpha(theme.palette.success.light, 0.7);
            break;
          case 'Cancelada':
            backgroundColor = theme.palette.error.main;
            borderColor = theme.palette.error.dark;
            textColor = '#ffffff';
            gradientColor = alpha(theme.palette.error.light, 0.7);
            break;
          default:
            backgroundColor = theme.palette.primary.main;
            borderColor = theme.palette.primary.dark;
            textColor = '#ffffff';
            gradientColor = alpha(theme.palette.primary.light, 0.7);
            break;
        }

        const patientName =
          [cita.paciente_nombres, cita.paciente_apellidos].filter(Boolean).join(' ').trim()
          || (cita.paciente_nombre && cita.paciente_nombre.trim())
          || ((typeof cita.paciente === 'string' && cita.paciente.trim()) ? cita.paciente : '');

        const mainTitleName = patientName || 'Cita sin paciente';
        const formattedTime = `${formatHMInTz(startDate, calendarTz)} - ${formatHMInTz(endDate, calendarTz)}`;

        return {
          id: cita.id_cita,
          title: `${mainTitleName}${cita.motivo_cita ? ` — ${cita.motivo_cita}` : ''}`,
          start: startIsoUtc,
          end: endIsoUtc,
          allDay: false,
          backgroundColor,
          borderColor,
          textColor,
          editable: ['Programada', 'Confirmada'].includes(cita.estado_cita),
          extendedProps: {
            ...cita,
            formattedTime,
            estado: cita.estado_cita,
            estado_cita: cita.estado_cita,
            gradientColor,
            _displayPatientName: patientName,
          },
        };
      })
      .filter(Boolean);
  }, [props.citas, showCancelled, calendarTz, theme.palette]);

  const handleEventClick = async (info) => {
    const ext = info.event.extendedProps || {};

    let patientDisplay =
      [ext.paciente_nombres, ext.paciente_apellidos].filter(Boolean).join(' ').trim()
      || (ext.paciente_nombre || '').trim()
      || (typeof ext.paciente === 'string' ? ext.paciente : '');

    let patientEmail = (ext.paciente_email || '').trim();
    let patientPhone = (ext.paciente_telefono || '').trim();

    if ((!patientDisplay || /^Dr/i.test(patientDisplay)) || !patientEmail || !patientPhone) {
      try {
        if (ext.paciente_id) {
          const r = await axiosInstance.get(`/pacientes/${ext.paciente_id}/`);
          const p = r.data || {};
          const byIdName = [p.nombres, p.apellidos].filter(Boolean).join(' ').trim();
          if (!patientDisplay || /^Dr/i.test(patientDisplay)) {
            patientDisplay = byIdName || patientDisplay;
          }
          if (!patientEmail && p.email) {
            patientEmail = p.email;
          }
          if (!patientPhone && p.telefono_principal) {
            patientPhone = p.telefono_principal;
          }
        }
      } catch {}
    }

    setSelectedEvent({
      id: info.event.id,
      title: info.event.title,
      start: info.event.start,
      end: info.event.end,
      extendedProps: {
        ...ext,
        _resolvedPatientName: patientDisplay,
        _resolvedPatientEmail: patientEmail,
        _resolvedPatientPhone: patientPhone,
      }
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => setDialogOpen(false);

  const handleEventDrop = async (info) => {
    const { event } = info;
    const eventId = event.id;
    const newStart = event.start;

    if (!['Programada', 'Confirmada'].includes(event.extendedProps.estado_cita)) {
      info.revert();
      showNotification('Solo se pueden mover citas programadas o confirmadas', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        info.revert();
        showNotification('No se encontró token de autenticación', 'error');
        return;
      }

      const response = await axiosInstance.get(`/citas/${eventId}/`);
      const citaData = response.data;

      await axiosInstance.put(`/citas/${eventId}/`, {
        ...citaData,
        fecha_hora_cita: newStart.toISOString()
      }, {
        headers: { 'Authorization': `Token ${token}` }
      });

      showNotification('Cita actualizada correctamente');
      onListChange?.();

    } catch (error) {
      console.error('Error al actualizar la cita:', error);
      info.revert();
      showNotification('Error al actualizar la cita', 'error');
    }
  };

  const handleEventResize = async (info) => {
    const { event } = info;
    const eventId = event.id;
    const newStart = event.start;
    const newEnd = event.end;

    const durationInMinutes = Math.round((newEnd - newStart) / (1000 * 60));

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        info.revert();
        showNotification('No se encontró token de autenticación', 'error');
        return;
      }

      const response = await axiosInstance.get(`/citas/${eventId}/`);
      const citaData = response.data;

      await axiosInstance.put(`/citas/${eventId}/`, {
        ...citaData,
        duracion_minutos: durationInMinutes
      });

      showNotification(`Duración actualizada a ${durationInMinutes} minutos`);
      onListChange?.();

    } catch (error) {
      console.error('Error al actualizar la duración:', error);
      info.revert();
      showNotification('Error al actualizar la duración', 'error');
    }
  };

  const handleCompleteAppointment = async () => {
    if (!selectedEvent) return;
    setCompletingAppointment(true);

    try {
      await axiosInstance.patch(`/citas/${selectedEvent.id}/`, { estado_cita: 'Completada' });
      showNotification('¡Cita marcada como completada!');
      handleCloseDialog();
      onListChange?.();
    } catch (error) {
      console.error('Error al completar la cita:', error?.response?.data || error);
      showNotification(
        error?.response?.data?.error || 'Hubo un error al marcar la cita como completada.',
        'error'
      );
    } finally {
      setCompletingAppointment(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedEvent) return;

    if (!window.confirm('¿Estás seguro de que quieres cancelar esta cita?')) return;

    try {
      await axiosInstance.patch(`/citas/${selectedEvent.id}/`, { estado_cita: 'Cancelada' });
      showNotification('¡Cita cancelada con éxito!');
      handleCloseDialog();
      onListChange?.();
    } catch (error) {
      console.error('Error al cancelar la cita:', error?.response?.data || error);
      showNotification(
        error?.response?.data?.error || 'Hubo un error al cancelar la cita.',
        'error'
      );
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Intl.DateTimeFormat('es-HN', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
      timeZone: calendarTz || undefined,
    }).format(new Date(date));
  };

  const renderEventContent = (eventInfo) => {
    const { event } = eventInfo;
    const { extendedProps } = event;
    const isTimeGridView = eventInfo.view.type.includes('timeGrid');
    const showDrag = ['Programada', 'Confirmada'].includes(extendedProps.estado);

    if (isTimeGridView) {
      return (
        <div style={{
          padding: isMobile ? '4px 6px' : '6px 8px',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '10px',
          background: `linear-gradient(135deg, ${event.backgroundColor} 0%, ${extendedProps.gradientColor} 100%)`,
          boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
          border: 'none',
          position: 'relative'
        }}>
          {showDrag && (
            isMobile ? (
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2px', opacity: 0.85 }}>
                <DragIndicatorIcon style={{ fontSize: 16, color: event.textColor }} />
              </div>
            ) : (
              <div style={{ position: 'absolute', top: 4, right: 6, opacity: 0.7 }}>
                <DragIndicatorIcon style={{ fontSize: 16, color: event.textColor }} />
              </div>
            )
          )}

          <div style={{
            fontWeight: 'bold',
            fontSize: isMobile ? '0.75rem' : '0.85rem',
            marginBottom: isMobile ? '2px' : '2px',
            color: event.textColor
          }}>
            {extendedProps.formattedTime}
          </div>

          {isMobile ? (
            <div style={{
              fontWeight: 'bold',
              fontSize: '0.75rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: event.textColor
            }}>
              {event.title}
            </div>
          ) : (
            <>
              {extendedProps.motivo_cita && (
                <div style={{
                  fontSize: '0.75rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  marginBottom: '2px',
                  color: event.textColor,
                  opacity: 0.95
                }}>
                  {extendedProps.motivo_cita}
                </div>
              )}

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '4px'
              }}>
                <div style={{
                  backgroundColor: 'rgba(255,255,255,0.3)',
                  borderRadius: '14px',
                  padding: '2px 8px',
                  fontSize: '0.7rem',
                  fontWeight: 'bold',
                  color: event.textColor
                }}>
                  {extendedProps.estado}
                </div>

                <div style={{
                  fontWeight: 'bold',
                  fontSize: '0.7rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  textAlign: 'right',
                  paddingLeft: '8px',
                  maxWidth: '60%',
                  color: event.textColor
                }}>
                  {event.title}
                </div>
              </div>
            </>
          )}
        </div>
      );
    } else {
      return (
        <div style={{
          padding: '4px 6px',
          height: '100%',
          background: `linear-gradient(135deg, ${event.backgroundColor} 0%, ${extendedProps.gradientColor} 100%)`,
          borderRadius: '8px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
          border: 'none'
        }}>
          <div style={{
            fontWeight: 'bold',
            fontSize: isMobile ? '0.75rem' : '0.75rem',
            color: event.textColor
          }}>
            {extendedProps.formattedTime}
          </div>

          <div style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: isMobile ? '0.75rem' : '0.75rem',
            color: event.textColor
          }}>
            {event.title}
          </div>
        </div>
      );
    }
  };

  const formatDateRange = (start, end) => {
    const monthNames = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

    if (currentView === 'dayGridMonth') {
      if (calendarRef.current) {
        try {
          const calendarApi = calendarRef.current.getApi();
          const currentDate = calendarApi.getDate();
          const monthName = monthNames[currentDate.getMonth()];
          return `${monthName} de ${currentDate.getFullYear()}`;
        } catch (error) {
          const monthName = monthNames[start.getMonth()];
          return `${monthName} de ${start.getFullYear()}`;
        }
      } else {
        const monthName = monthNames[start.getMonth()];
        return `${monthName} de ${start.getFullYear()}`;
      }
    } else if (currentView === 'timeGridDay') {
      const day = start.getDate();
      const monthName = monthNames[start.getMonth()];
      return `${day} de ${monthName} de ${start.getFullYear()}`;
    } else {
      const startDay = start.getDate();
      const endDate = new Date(end);
      endDate.setDate(endDate.getDate() - 1);
      const endDay = endDate.getDate();
      const startMonthName = monthNames[start.getMonth()];
      const endMonthName = monthNames[endDate.getMonth()];
      if (start.getMonth() === endDate.getMonth()) {
        return `${startDay} - ${endDay} de ${startMonthName} de ${start.getFullYear()}`;
      } else {
        return `${startDay} ${startMonthName} - ${endDay} ${endMonthName} de ${start.getFullYear()}`;
      }
    }
  };

  useEffect(() => {
    setFadeIn(true);
    if (isMobile) {
      const timer = setTimeout(() => setShowScrollHelper(false), 8000);
      return () => clearTimeout(timer);
    }
  }, [isMobile]);

  useEffect(() => {
    if (fadeIn) {
      const fixCalendar = () => {
        setTimeout(() => {
          try {
            const chipContainer = document.querySelector('.date-range-chip-container');
            if (chipContainer) chipContainer.style.top = '10px';

            const toolbarTitle = document.querySelector('.fc-toolbar-title');
            if (toolbarTitle) {
              setDateRangeText(toolbarTitle.textContent);
              toolbarTitle.style.visibility = 'hidden';
              toolbarTitle.style.position = 'absolute';
              toolbarTitle.style.width = '0';
              toolbarTitle.style.height = '0';
              toolbarTitle.style.overflow = 'hidden';
            }
          } catch (error) {
            console.error('Error al ajustar el calendario:', error);
          }
        }, 100);
      };

      fixCalendar();
      const observer = new MutationObserver(fixCalendar);
      const calendarElement = document.querySelector('.fc');
      if (calendarElement) {
        observer.observe(calendarElement, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'style']
        });
      }
      return () => observer.disconnect();
    }
  }, [fadeIn, theme, currentView]);

  const ext = selectedEvent?.extendedProps || {};

  const displayPatient =
    (ext._resolvedPatientName && String(ext._resolvedPatientName).trim())
    || (ext._displayPatientName && String(ext._displayPatientName).trim())
    || [ext.paciente_nombres, ext.paciente_apellidos].filter(Boolean).join(' ').trim()
    || (ext.paciente_nombre || '').trim()
    || (typeof ext.paciente === 'string' ? ext.paciente : '')
    || 'Cita sin paciente';

  const displayPatientEmail =
    (ext._resolvedPatientEmail && String(ext._resolvedPatientEmail).trim())
    || (ext.paciente_email || '').trim()
    || '';

  const displayPatientPhone =
    (ext._resolvedPatientPhone && String(ext._resolvedPatientPhone).trim())
    || (ext.paciente_telefono || '').trim()
    || '';

  return (
    <Fade in={fadeIn} timeout={800}>
      <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
        <style>{`
          /* Base */
          .fc {
            display: flex;
            flex-direction: column;
            height: 100%;
            font-family: ${theme.typography.fontFamily};
          }

          .fc-view-harness {
            margin-top: 10px !important;
            flex-grow: 1;
            overflow: auto !important;
            -webkit-overflow-scrolling: touch !important;
            background-color: ${theme.palette.background.paper};
            border-radius: 20px;
            border: 2px solid ${alpha(theme.palette.divider, 0.2)};
            box-shadow: 0 8px 32px ${alpha(theme.palette.common.black, 0.1)};
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .fc-view-harness::-webkit-scrollbar { width: 0; height: 0; display: none; }
          .fc-view { min-width: auto !important; }

          .fc-toolbar-title {
            visibility: hidden !important;
            height: 0 !important;
            overflow: hidden !important;
            position: absolute !important;
          }

          /* Header días (escritorio) */
          .fc-col-header-cell {
            background: linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.light, 0.12)} 100%);
            border: none !important;
            padding: 16px 8px !important;
          }
          .fc-col-header-cell-cushion {
            font-size: 1.3rem !important;
            font-weight: 800 !important;
            text-transform: uppercase;
            color: ${theme.palette.primary.dark};
            padding: 16px 8px !important;
            letter-spacing: 0.5px;
            display: block !important;
            text-decoration: none !important;
          }

          /* Toolbar contenedor */
          .fc .fc-toolbar.fc-header-toolbar {
            height: 60px !important;
            min-height: 60px !important;
            margin-bottom: 1em !important;
            padding: 0 16px !important;
            position: relative !important;
            background-color: ${alpha(theme.palette.primary.light, 0.05)};
            border-radius: 16px 16px 0 0;
            border-bottom: 1px solid ${alpha(theme.palette.divider, 0.1)};
            display: flex !important;
            align-items: center !important;
          }

          /* Ocultamos toolbar original */
          .fc-toolbar-chunk { visibility: hidden !important; position: absolute !important; width: 0 !important; height: 0 !important; overflow: hidden !important; }

          /* Botones custom contenedores */
          .custom-nav-buttons-container {
            position: absolute !important; top: 10px !important; left: 16px !important; z-index: 10 !important; display: flex !important; gap: 8px !important;
          }
          .custom-view-buttons-container {
            position: absolute !important; top: 10px !important; right: 16px !important; z-index: 10 !important; display: flex !important; gap: 8px !important;
          }

          /* Botones custom (escritorio) */
          .custom-calendar-button {
            height: 36px !important;
            min-width: 36px !important;
            padding: 0 12px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            text-transform: capitalize !important;
            font-weight: 700 !important;
            box-shadow: 0 4px 12px ${alpha(theme.palette.primary.main, 0.2)} !important;
            font-size: 0.95rem !important;
            border-radius: 16px !important;
            transition: all 0.3s ease !important;
            border: none !important;
            letter-spacing: 0.3px;
            background: linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%) !important;
            color: white !important;
            cursor: pointer !important;
          }
          .custom-calendar-button:hover {
            box-shadow: 0 6px 20px ${alpha(theme.palette.primary.main, 0.35)} !important;
            transform: translateY(-3px) !important;
            background: linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%) !important;
          }
          .custom-calendar-button.active {
            background: linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${alpha(theme.palette.primary.dark, 0.8)} 100%) !important;
            box-shadow: 0 2px 8px ${alpha(theme.palette.common.black, 0.3)} inset !important;
            transform: translateY(0px) !important;
          }

          /* Hoy destacado */
          .fc-day-today {
            background: linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.15)} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%) !important;
            border: 3px solid ${theme.palette.primary.main} !important;
            position: relative;
            box-shadow: 0 4px 16px ${alpha(theme.palette.primary.main, 0.25)} !important;
          }
          .fc-day-today::before {
            content: '';
            position: absolute; inset: 0;
            background: linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 100%);
            pointer-events: none; z-index: 0;
          }

          /* Eventos */
          .fc-event {
            border-radius: 12px !important;
            overflow: hidden;
            box-shadow: 0 4px 12px ${alpha(theme.palette.common.black, 0.18)} !important;
            border: none !important;
            padding: 0 !important;
            cursor: pointer;
            transition: all 0.3s ease !important;
          }
          .fc-event:hover { transform: translateY(-4px) scale(1.02) !important; box-shadow: 0 8px 20px ${alpha(theme.palette.common.black, 0.25)} !important; }

          /* Indicador ahora */
          .fc-timegrid-now-indicator-line { border-color: ${theme.palette.error.main}; border-width: 3px; box-shadow: 0 0 12px ${alpha(theme.palette.error.main, 0.7)}; }
          .fc-timegrid-now-indicator-arrow { border-color: ${theme.palette.error.main}; border-width: 6px; filter: drop-shadow(0 0 4px ${alpha(theme.palette.error.main, 0.7)}); }

          /* Etiquetas de hora (escritorio) */
          .fc-timegrid-slot-label-cushion,
          .fc-timegrid-axis-cushion {
            font-size: 0.85rem !important;
            padding: 6px 3px !important;
            font-weight: 700 !important;
            color: ${theme.palette.text.secondary};
          }

          /* Líneas y frames */
          .fc-theme-standard td, .fc-theme-standard th { border-color: ${alpha(theme.palette.divider, 0.15)}; border-width: 1px; }
          .fc-timegrid-col-frame { background: ${alpha(theme.palette.background.paper, 0.5)}; }
          .fc-scrollgrid { border-radius: 20px; overflow: hidden; border-color: ${alpha(theme.palette.divider, 0.2)}; border-width: 2px; }
          .fc-timegrid-slot-minor { border-top-style: dashed; border-top-color: ${alpha(theme.palette.divider, 0.2)}; border-top-width: 1px; }

          /* Vista mensual (escritorio) */
          .fc-dayGridMonth-view .fc-daygrid-day { min-height: 150px !important; height: auto !important; }
          .fc-dayGridMonth-view .fc-daygrid-body { height: 100% !important; }
          .fc-dayGridMonth-view .fc-scrollgrid-sync-table { height: 100% !important; }
          .fc-dayGridMonth-view .fc-daygrid-day-events { min-height: 80px !important; padding: 4px !important; }
          .fc-dayGridMonth-view .fc-daygrid-day-number { font-size: 1.2rem !important; font-weight: 700 !important; padding: 8px 12px !important; }

          /* Chip rango */
          .date-range-chip-container {
            position: absolute !important;
            top: 10px !important;
            left: 50% !important;
            transform: translateX(-50%) !important;
            z-index: 10 !important;
            pointer-events: none !important;
          }

          /* Solo mobile */
          @media (max-width: 600px) {
            /* Toolbar más compacta */
            .fc .fc-toolbar.fc-header-toolbar {
              height: 34px !important;
              min-height: 34px !important;
              margin-bottom: 6px !important;
              padding: 0 6px !important;
              border-radius: 12px 12px 0 0 !important;
            }

            /* Contenedores botones */
            .custom-nav-buttons-container,
            .custom-view-buttons-container { top: 4px !important; }
            .custom-nav-buttons-container { left: 6px !important; gap: 4px !important; }
            .custom-view-buttons-container { right: 6px !important; gap: 4px !important; }

            /* Botones MÁS pequeños (solo mobile) */
            .custom-calendar-button {
              height: 20px !important;
              min-width: 20px !important;
              padding: 0 4px !important;
              font-size: 0.56rem !important;
              border-radius: 6px !important;
              box-shadow: 0 2px 5px ${alpha(theme.palette.primary.main, 0.2)} !important;
            }
            /* Flechas redondas mini */
            .custom-nav-buttons-container .custom-calendar-button {
              width: 20px !important;
              min-width: 20px !important;
              padding: 0 !important;
              border-radius: 50% !important;
            }

            /* Chip más pequeño */
            .date-range-chip-container { top: 4px !important; }
            .date-range-chip-container .MuiChip-root {
              min-width: 110px !important;
              padding: 4px 6px !important;
              border-radius: 12px !important;
              box-shadow: 0 2px 6px rgba(0,0,0,0.12) !important;
            }
            .date-range-chip-container .MuiChip-label {
              font-size: 0.68rem !important;
              padding: 0 6px !important;
              font-weight: 700 !important;
            }
            .date-range-chip-container .MuiChip-icon {
              margin-left: 2px !important;
              width: 12px !important;
              height: 12px !important;
            }

            /* Texto de los días más pequeño (formato igual) */
            .fc-col-header-cell { padding: 6px 4px !important; }
            .fc-col-header-cell-cushion {
              font-size: 0.78rem !important;
              font-weight: 700 !important;
              text-transform: none !important;
              letter-spacing: 0 !important;
              padding: 6px 4px !important;
            }

            /* Vista mensual más compacta */
            .fc-daygrid-day { min-height: 72px !important; border-width: 1px !important; }
            .fc-dayGridMonth-view .fc-daygrid-day-number,
            .fc-daygrid-day-number { font-size: 0.82rem !important; padding: 4px !important; }

            /* Altura de slots (mobile) */
            .fc-timegrid-slot { height: 44px !important; }

            /* EJE DE HORA: más estrecho y tipografía más pequeña en mobile */
            .fc-timegrid-axis { min-width: 54px !important; width: 54px !important; }
            .fc-timegrid-slot-label-cushion,
            .fc-timegrid-axis-cushion {
              font-size: 0.74rem !important;
              padding: 3px 3px !important;
              font-weight: 800 !important;
              text-shadow: none !important;
              color: ${theme.palette.text.primary} !important;
            }

            /* Eventos menos sombra en móvil */
            .fc-event { box-shadow: 0 2px 6px ${alpha(theme.palette.common.black, 0.18)} !important; }

            /* HABILITAR SCROLL HORIZONTAL SOLO EN MOBILE (wrapper externo) */
            .calendar-x-wrap {
              overflow-x: auto !important;
              -webkit-overflow-scrolling: touch !important;
            }
            /* No dejes que el scroller interno bloquee el desborde horizontal */
            .calendar-x-wrap .fc-view-harness {
              overflow-x: visible !important;
            }
            .calendar-x-wrap .fc .fc-scroller-harness .fc-scroller {
              overflow-x: visible !important;
            }
            /* Fuerza ancho mínimo real de toda la grilla semanal para que se desborde */
            .calendar-x-wrap .fc-timeGridWeek-view .fc-scrollgrid,
            .calendar-x-wrap .fc-timeGridWeek-view .fc-timegrid-body,
            .calendar-x-wrap .fc-timeGridWeek-view .fc-scrollgrid-sync-table,
            .calendar-x-wrap .fc-timeGridWeek-view .fc-col-header,
            .calendar-x-wrap .fc-timeGridWeek-view .fc-col-header table {
              min-width: 980px !important; /* ajusta si necesitas más/menos */
            }
          }

          @keyframes pulse {
            0% { transform: translateX(-50%) scale(1); }
            50% { transform: translateX(-50%) scale(1.05); }
            100% { transform: translateX(-50%) scale(1); }
          }
        `}</style>

        {/* Chip rango fechas */}
        <Box
          className="date-range-chip-container"
          sx={{
            position: 'absolute',
            top: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: 'none'
          }}>
          <Chip
            icon={<CalendarTodayIcon />}
            label={dateRangeText}
            color="primary"
            sx={{
              fontWeight: 700,
              fontSize: '1.1rem',
              padding: '20px 16px',
              borderRadius: '30px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: 'white',
              pointerEvents: 'auto',
              minWidth: '280px',
              justifyContent: 'center',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              },
              '& .MuiChip-label': {
                padding: '0 12px',
                color: 'white',
                fontWeight: 700,
                textShadow: '0 1px 2px rgba(0,0,0,0.2)'
              },
              '& .MuiChip-icon': {
                color: 'white',
                marginLeft: '8px'
              }
            }}
          />
        </Box>

        {/* Botones navegación */}
        <Box
          className="custom-nav-buttons-container"
          sx={{
            position: 'absolute',
            top: '10px',
            left: '16px',
            zIndex: 10,
            display: 'flex',
            gap: 1
          }}>
          <Button
            className="custom-calendar-button"
            onClick={() => { calendarRef.current.getApi().prev(); }}
            sx={{ minWidth: '36px', width: '36px', padding: 0, borderRadius: '50%' }}
          >
            <ArrowBackIcon fontSize="small" />
          </Button>

          <Button
            className="custom-calendar-button"
            onClick={() => { calendarRef.current.getApi().next(); }}
            sx={{ minWidth: '36px', width: '36px', padding: 0, borderRadius: '50%' }}
          >
            <ArrowForwardIcon fontSize="small" />
          </Button>

          <Button
            className="custom-calendar-button"
            onClick={() => { calendarRef.current.getApi().today(); }}
            sx={{ ml: 1 }}
          >
            Hoy
          </Button>
        </Box>

        {/* Botones vista */}
        <Box
          className="custom-view-buttons-container"
          sx={{
            position: 'absolute',
            top: '10px',
            right: '16px',
            zIndex: 10,
            display: 'flex',
            gap: 1
          }}>
          <Button
            variant="contained"
            color="primary"
            className={`custom-calendar-button month-button ${currentView === 'dayGridMonth' ? 'active' : ''}`}
            onClick={() => { calendarRef.current.getApi().changeView('dayGridMonth'); }}
            sx={{
              background: currentView === 'dayGridMonth'
                ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${alpha(theme.palette.primary.dark, 0.8)} 100%)`
                : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                transform: 'translateY(-3px)',
                boxShadow: '0 6px 16px rgba(0,0,0,0.12)'
              }
            }}
          >
            Mes
          </Button>

          <Button
            variant="contained"
            color="primary"
            className={`custom-calendar-button week-button ${currentView === 'timeGridWeek' ? 'active' : ''}`}
            onClick={() => { calendarRef.current.getApi().changeView('timeGridWeek'); }}
            sx={{
              background: currentView === 'timeGridWeek'
                ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${alpha(theme.palette.primary.dark, 0.8)} 100%)`
                : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                transform: 'translateY(-3px)',
                boxShadow: '0 6px 16px rgba(0,0,0,0.12)'
              }
            }}
          >
            Semana
          </Button>

          <Button
            variant="contained"
            color="primary"
            className={`custom-calendar-button day-button ${currentView === 'timeGridDay' ? 'active' : ''}`}
            onClick={() => { calendarRef.current.getApi().changeView('timeGridDay'); }}
            sx={{
              background: currentView === 'timeGridDay'
                ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${alpha(theme.palette.primary.dark, 0.8)} 100%)`
                : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                transform: 'translateY(-3px)',
                boxShadow: '0 6px 16px rgba(0,0,0,0.12)'
              }
            }}
          >
            Día
          </Button>
        </Box>

        {/* Wrapper para habilitar scroll horizontal solo en mobile */}
        <Box className="calendar-x-wrap" sx={{ height: '100%', width: '100%' }}>
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, luxon3Plugin]}
            initialView={isMobile ? 'timeGridDay' : 'timeGridWeek'}
            headerToolbar={{ left: ' ', center: ' ', right: ' ' }}
            firstDay={1}
            locale={esLocale}
            buttonText={{ today: 'Hoy', month: 'Mes', week: 'Semana', day: 'Día' }}
            events={eventosDelCalendario}
            eventClick={handleEventClick}
            eventContent={renderEventContent}
            height="100%"
            allDaySlot={false}
            timeZone={calendarTz}
            slotMinTime={isMobile ? '07:00:00' : '06:00:00'}
            slotMaxTime={isMobile ? '20:00:00' : '22:00:00'}
            nowIndicator={true}
            scrollTime={isMobile ? '09:00:00' : '08:00:00'}
            expandRows={true}
            slotEventOverlap={false}
            eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
            slotLabelFormat={
              isMobile
                ? { hour: '2-digit', minute: '2-digit', hour12: false, omitZeroMinute: false }
                : { hour: '2-digit', minute: '2-digit', hour12: false }
            }
            dayHeaderFormat={{ weekday: 'long', day: 'numeric', omitCommas: true }}
            windowResizeDelay={200}
            titleFormat={{ month: 'long', year: 'numeric' }}
            slotDuration="00:30:00"
            stickyHeaderDates={true}
            handleWindowResize={true}
            contentHeight="auto"
            editable={true}
            eventDrop={handleEventDrop}
            eventResize={handleEventResize}
            droppable={true}
            views={{
              dayGridMonth: {
                dayHeaderFormat: { weekday: 'long', omitCommas: true }
              },
              timeGridWeek: {
                dayHeaderFormat: { weekday: 'long', day: 'numeric', omitCommas: true }
              },
              timeGridDay: {
                dayHeaderFormat: { weekday: 'long', day: 'numeric', omitCommas: true }
              }
            }}
            datesSet={(dateInfo) => {
              if (dateInfo.view.type === 'dayGridMonth') {
                if (calendarRef.current) {
                  const calendarApi = calendarRef.current.getApi();
                  const currentDate = calendarApi.getDate();
                  const monthNames = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
                  const monthName = monthNames[currentDate.getMonth()];
                  setDateRangeText(`${monthName} de ${currentDate.getFullYear()}`);
                } else {
                  const monthNames = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
                  const monthName = monthNames[dateInfo.start.getMonth()];
                  setDateRangeText(`${monthName} de ${dateInfo.start.getFullYear()}`);
                }
              } else {
                const formattedDateRange = formatDateRange(dateInfo.start, dateInfo.end);
                setDateRangeText(formattedDateRange);
              }

              setCurrentView(dateInfo.view.type);

              setTimeout(() => {
                const chipContainer = document.querySelector('.date-range-chip-container');
                if (chipContainer) chipContainer.style.top = '10px';

                const monthButton = document.querySelector('.month-button');
                const weekButton = document.querySelector('.week-button');
                const dayButton = document.querySelector('.day-button');

                if (monthButton && weekButton && dayButton) {
                  monthButton.classList.toggle('active', dateInfo.view.type === 'dayGridMonth');
                  weekButton.classList.toggle('active', dateInfo.view.type === 'timeGridWeek');
                  dayButton.classList.toggle('active', dateInfo.view.type === 'timeGridDay');
                }

                const toolbarTitle = document.querySelector('.fc-toolbar-title');
                if (toolbarTitle) {
                  toolbarTitle.style.visibility = 'hidden';
                  toolbarTitle.style.position = 'absolute';
                  toolbarTitle.style.width = '0';
                  toolbarTitle.style.height = '0';
                  toolbarTitle.style.overflow = 'hidden';
                }
              }, 100);
            }}
            eventDidMount={(info) => {
              if (info.view.type === 'dayGridMonth') {
                const backgroundColor = info.event.backgroundColor;
                const textColor = info.event.textColor;
                if (info.el) {
                  info.el.style.backgroundColor = backgroundColor;
                  info.el.style.borderColor = info.event.borderColor;
                  info.el.style.color = textColor;
                }
              }
              if (info.el) {
                info.el.title = `${info.event.title} - ${info.event.extendedProps.motivo_cita || 'Sin motivo especificado'}`;
              }
            }}
          />
        </Box>

        {/* Helper scroll mobile */}
        {isMobile && showScrollHelper && (
          <Paper
            elevation={4}
            sx={{
              position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
              padding: '10px 20px', borderRadius: '24px', backgroundColor: alpha(theme.palette.primary.main, 0.9),
              color: 'white', display: 'flex', alignItems: 'center', gap: 1.5,
              boxShadow: '0 4px 15px rgba(0,0,0,0.25)', zIndex: 1000,
              animation: 'pulse 2s infinite'
            }}
          >
            <TouchAppIcon fontSize="small" />
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Desliza para navegar
            </Typography>
          </Paper>
        )}

        {/* Diálogo */}
        {selectedEvent && (
          <Dialog
            open={dialogOpen}
            onClose={handleCloseDialog}
            fullScreen={isMobile}
            PaperProps={{ sx: { borderRadius: isMobile ? 0 : '24px', overflow: 'hidden', boxShadow: '0 15px 50px rgba(0,0,0,0.2)' } }}
          >
            <DialogTitle component="div" sx={{
              background: selectedEvent.extendedProps?.estado === 'Programada'
                ? `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`
                : selectedEvent.extendedProps?.estado === 'Confirmada'
                ? `linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)`
                : selectedEvent.extendedProps?.estado === 'Completada'
                ? `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`
                : selectedEvent.extendedProps?.estado === 'Cancelada'
                ? `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`
                : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: 'primary.contrastText',
              py: isMobile ? 2 : 2.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 800, letterSpacing: '-0.5px' }}>
                Detalles de la Cita
              </Typography>
              <IconButton edge="end" color="inherit" onClick={handleCloseDialog} aria-label="close"
                sx={{ backgroundColor: 'rgba(255,255,255,0.15)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.25)' } }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: isMobile ? 3 : 4 }}>
              <DialogContentText component="div" sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: 'primary.main', mr: 2.5, width: 56, height: 56, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                      <PersonIcon fontSize="large" />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Paciente</Typography>
                      <Typography variant="h6" fontWeight="bold">{displayPatient}</Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: 'info.main', mr: 2.5, width: 56, height: 56, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                      <EventIcon fontSize="large" />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Fecha y Hora</Typography>
                      <Typography variant="h6" fontWeight="bold">{formatDate(selectedEvent.start)}</Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                        <Chip label={selectedEvent.extendedProps?.modalidad || 'Presencial'} color="primary" variant="outlined" sx={{ fontWeight: 700 }} />
                        <Chip
                          label={selectedEvent.extendedProps?.estado_pago === 'Pagado' ? 'Pagado (PayPal)' : 'Pago en consultorio'}
                          color={selectedEvent.extendedProps?.estado_pago === 'Pagado' ? 'success' : 'warning'}
                          variant="outlined"
                          sx={{ fontWeight: 700 }}
                        />
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: 'success.main', mr: 2.5, width: 56, height: 56, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                      <SubjectIcon fontSize="large" />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Motivo</Typography>
                      <Typography variant="h6" fontWeight="bold">{selectedEvent.extendedProps?.motivo_cita}</Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: 'secondary.main', mr: 2.5, width: 56, height: 56, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                      <EmailIcon fontSize="large" />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Correo del paciente</Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {displayPatientEmail
                          ? <a href={`mailto:${displayPatientEmail}`} style={{ color: 'inherit', textDecoration: 'none' }}>{displayPatientEmail}</a>
                          : 'No registrado'}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: 'secondary.main', mr: 2.5, width: 56, height: 56, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                      <PhoneIcon fontSize="large" />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Teléfono del paciente</Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {displayPatientPhone
                          ? <a href={`tel:${displayPatientPhone}`} style={{ color: 'inherit', textDecoration: 'none' }}>{displayPatientPhone}</a>
                          : 'No registrado'}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Chip
                      label={selectedEvent.extendedProps?.estado}
                      color={
                        selectedEvent.extendedProps?.estado === 'Programada' ? 'info' :
                        selectedEvent.extendedProps?.estado === 'Confirmada' ? 'secondary' :
                        selectedEvent.extendedProps?.estado === 'Completada' ? 'success' :
                        selectedEvent.extendedProps?.estado === 'Cancelada' ? 'error' : 'default'
                      }
                      sx={{
                        fontWeight: 'bold',
                        boxShadow: '0 3px 8px rgba(0,0,0,0.12)',
                        padding: '24px 16px',
                        borderRadius: '16px',
                        fontSize: '1rem',
                        '& .MuiChip-label': { padding: '0 12px' }
                      }}
                    />
                  </Box>
                </Box>
              </DialogContentText>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 3, flexDirection: 'column', gap: 2 }}>
              {showReminderButton && ['Programada', 'Confirmada'].includes(selectedEvent.extendedProps?.estado_cita) && onSendReminder && (
                <Button
                  fullWidth
                  variant="outlined"
                  color="info"
                  startIcon={sendingReminder === selectedEvent.id ? <CircularProgress size={24} /> : <EmailIcon />}
                  onClick={() => onSendReminder(selectedEvent.id)}
                  disabled={sendingReminder === selectedEvent.id}
                  sx={{
                    borderRadius: '16px', textTransform: 'none', fontWeight: 700, padding: '12px 24px',
                    fontSize: '1rem', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '2px solid',
                    '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 6px 16px rgba(0,0,0,0.12)' }
                  }}
                >
                  {sendingReminder === selectedEvent.id ? 'Enviando...' : 'Enviar recordatorio'}
                </Button>
              )}
              <Box sx={{ width: '100%', display: 'flex', gap: 2, flexDirection: isMobile ? 'column' : 'row' }}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleCompleteAppointment}
                  disabled={completingAppointment || !['Programada', 'Confirmada'].includes(selectedEvent.extendedProps?.estado_cita)}
                  sx={{
                    borderRadius: '16px', textTransform: 'none', fontWeight: 700, padding: '12px 24px',
                    fontSize: '1rem', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '2px solid',
                    '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 6px 16px rgba(0,0,0,0.12)' }
                  }}
                >
                  {completingAppointment ? 'Procesando...' : 'Completar'}
                </Button>
                <Button
                  fullWidth
                  component={Link}
                  to={`/doctor/citas/${selectedEvent.id}/edit`}
                  variant="outlined"
                  color="primary"
                  startIcon={<EditIcon />}
                  sx={{
                    borderRadius: '16px', textTransform: 'none', fontWeight: 700, padding: '12px 24px',
                    fontSize: '1rem', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '2px solid',
                    '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 6px 16px rgba(0,0,0,0.12)' }
                  }}
                >
                  Editar
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDelete}
                  sx={{
                    borderRadius: '16px', textTransform: 'none', fontWeight: 700, padding: '12px 24px',
                    fontSize: '1rem', boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
                    '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 6px 16px rgba(0,0,0,0.18)' }
                  }}
                >
                  Eliminar
                </Button>
              </Box>
              <Button
                fullWidth
                onClick={handleCloseDialog}
                sx={{ mt: 1, borderRadius: '16px', textTransform: 'none', fontWeight: 600, padding: '10px 20px', fontSize: '0.95rem' }}
              >
                Cerrar
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            variant="filled"
            sx={{
              width: '100%',
              boxShadow: '0 6px 24px rgba(0,0,0,0.2)',
              borderRadius: '16px',
              fontSize: '1rem',
              '& .MuiAlert-icon': { fontSize: '24px' }
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </Fade>
  );
};

export default CitaCalendar;
